import { HttpErrorResponse } from '@angular/common/http';
import { NormalizedError } from './error.types';

export function normalizeError(err: unknown): NormalizedError {
    const fallback: NormalizedError = {
        message: 'i18n:errors.unexpected',
        category: 'server',
        severity: 'error',
        handled: false,
    };

    if (!(err instanceof HttpErrorResponse)) {
        return fallback;
    }

    const res = err;
    const payload = res.error;
    const status = res.status;

    // Netzwerk / CORS etc.
    if (status === 0) {
        return {
            message: 'i18n:errors.noConnection',
            status,
            category: 'network',
            severity: 'error',
            handled: false,
            raw: payload,
        };
    }

    // ProblemDetails-ähnlich?
    const fieldErrors =
        payload?.errors ||
        payload?.fieldErrors ||
        payload?.validationErrors ||
        undefined;

    // ✅ 400 mit Feldfehlern → handled validation
    if (status === 400 && fieldErrors && Object.keys(fieldErrors).length > 0) {
        const firstMsg = extractFirstFieldError(fieldErrors)
            || payload?.detail
            || payload?.title
            || 'i18n:errors.validation';
        return {
            message: firstMsg,
            code: payload?.code ?? status,
            status,
            fieldErrors,
            raw: payload,
            handled: true,
            category: 'validation',
            severity: 'warning',
            traceId: payload?.traceId,
        };
    }

    // string/Message-Felder aus Backends
    const serverMessage =
        (typeof payload === 'string' && payload) ||
        payload?.message ||
        payload?.detail ||
        payload?.title ||
        (status ? `HTTP ${status}` : undefined);

    const hasExceptionSignature =
        (typeof payload?.title === 'string' && payload.title.startsWith('System.')) ||
        !!payload?.exception;

    const rfcServerError = typeof payload?.type === 'string' &&
        payload.type.includes('rfc9110#section-15.6.1'); // 5xx section

    // ❌ Unhandled Server Error (5xx/Exception/RFC 15.6.1)
    if (status >= 500 || hasExceptionSignature || rfcServerError) {
        const rawDetails: string | undefined = payload?.exception?.details;
        const prettyStack = rawDetails ? rawDetails.replace(/\\n/g, '\n') : undefined;

        return {
            message: buildGenericServerMessage(),
            code: payload?.code ?? status,
            status,
            raw: payload,
            handled: false,
            category: 'server',
            severity: 'error',
            traceId: payload?.traceId,
            stack: prettyStack, // <— hier rein
        };
    }

    // 4xx ohne Feldfehler → handled/domain
    if (status >= 400 && status < 500) {
        return {
            message: serverMessage || 'i18n:errors.requestFailed',
            code: payload?.code ?? status,
            status,
            raw: payload,
            handled: true, // <— hier: handled
            category: 'domain',
            severity: mapSeverity(status),
            traceId: payload?.traceId,
        };
    }

    // Fallback (z. B. exotische Status)
    return {
        message: serverMessage || fallback.message,
        code: payload?.code ?? status,
        status,
        raw: payload,
        handled: status < 500, // konservativ: <500 eher handled
        category: status >= 500 ? 'server' : 'domain',
        severity: status >= 500 ? 'error' : 'info',
        traceId: payload?.traceId,
    };
}

/** Holt die erste aussagekräftige Fehlermeldung aus einem errors-Dictionary. */
function extractFirstFieldError(errorsObj: any): string | undefined {
    // Struktur: { "General.Validation": ["Message1","Message2"], "field": ["..."] }
    for (const key of Object.keys(errorsObj)) {
        const val = errorsObj[key];
        if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'string' && val[0].trim()) {
            return val[0].trim();
        }
        if (typeof val === 'string' && val.trim()) {
            return val.trim();
        }
    }
    return undefined;
}

function mapSeverity(status: number | undefined): 'info' | 'warning' | 'error' {
    if (!status) return 'error';
    if (status >= 500) return 'error';
    if (status === 409 || status === 404) return 'info';
    if (status >= 400) return 'warning';
    return 'info';
}

function buildGenericServerMessage(): string {
    return 'i18n:errors.unexpectedShort';
    /*const base = 'Es ist ein unerwarteter Fehler aufgetreten.';
    return traceId
        ? `${base}\nTrace-ID: ${traceId}`
        : base;*/
}
